import { useState, useEffect } from "react";
import { Loader2, Info } from "lucide-react";
import { api, errMsg, money, fmtDate } from "../../lib/api";
import { track } from "../../lib/analytics";
import { Field, Consent, Success, useForm, validate } from "./Forms";

export function BookingForm({ exp, departureId, onDeparture }) {
  const deps = exp.departures || [];
  const fm = useForm({ departure_id: departureId || "", participant_count: 1, name: "", email: "", phone: "", special_requirements: "", consent: false });
  const [people, setPeople] = useState([]);
  const { f, set, errors } = fm;
  useEffect(() => { if (departureId) set("departure_id", departureId); }, [departureId]); // eslint-disable-line
  const dep = deps.find((d) => d.id === f.departure_id);
  const unit = dep ? dep.promo_price_minor || dep.price_minor || exp.base_price_minor : exp.base_price_minor;
  const n = Number(f.participant_count) || 0;
  useEffect(() => setPeople((p) => Array.from({ length: Math.max(0, n - 1) }, (_, i) => p[i] || { name: "", age: "" })), [n]);

  const submit = async (ev) => {
    ev.preventDefault();
    const e = validate(f, ["departure_id", "name", "email", "phone"]);
    if (n < 1) e.participant_count = "At least 1 participant";
    if (dep && n > dep.seats_left) e.participant_count = `Only ${dep.seats_left} seat(s) left`;
    if (exp.max_group && n > exp.max_group) e.participant_count = `Maximum ${exp.max_group} per booking`;
    fm.setErrors(e);
    if (Object.keys(e).length) return;
    fm.setBusy(true); fm.setError("");
    track("begin_checkout", { item_id: exp.id, category: exp.category });
    try {
      const participants = [{ name: f.name }, ...people.filter((p) => p.name).map((p) => ({ name: p.name, age: p.age ? Number(p.age) : null }))];
      const r = await api.post("/public/bookings", { ...f, participant_count: n, experience_id: exp.id, participants, source_page: window.location.pathname });
      fm.setDone(r.data);
      track("booking_request", { item_id: exp.id, category: exp.category }, `bk-${r.data.reference}`);
    } catch (err) { fm.setError(errMsg(err)); } finally { fm.setBusy(false); }
  };

  if (fm.done) return <Success title="Booking request received" text={fm.done.message} reference={fm.done.reference} testid="booking-success" />;
  if (!deps.some((d) => d.status === "open")) return <p className="text-sm text-[#333] border border-[#E5E5E5] p-4" data-testid="booking-no-departures">No open departures right now. Send an enquiry and we will let you know about the next dates.</p>;
  return (
    <form onSubmit={submit} noValidate className="space-y-4" data-testid="booking-form">
      <Field label="Departure date" id="bk-dep" error={errors.departure_id} required>
        <select id="bk-dep" className="field" value={f.departure_id} onChange={(e) => { set("departure_id", e.target.value); onDeparture?.(e.target.value); }} data-testid="booking-departure-select">
          <option value="">Select a date</option>
          {deps.map((d) => (
            <option key={d.id} value={d.id} disabled={d.status !== "open"}>
              {fmtDate(d.start_date)}{d.end_date && d.end_date !== d.start_date ? ` – ${fmtDate(d.end_date)}` : ""} · {d.status === "open" ? `${d.seats_left} seats left` : d.status.replace("_", " ").toUpperCase()}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Participants" id="bk-n" error={errors.participant_count} required>
        <input id="bk-n" type="number" min={exp.min_group || 1} max={Math.min(dep?.seats_left || 100, exp.max_group || 100)} className="field" value={f.participant_count} onChange={(e) => set("participant_count", e.target.value)} data-testid="booking-participants" />
      </Field>
      <Field label="Lead traveller name" id="bk-name" error={errors.name} required><input id="bk-name" className="field" value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="booking-name" /></Field>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Email" id="bk-email" error={errors.email} required><input id="bk-email" type="email" className="field" value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="booking-email" /></Field>
        <Field label="Phone" id="bk-phone" error={errors.phone} required><input id="bk-phone" type="tel" className="field" value={f.phone} onChange={(e) => set("phone", e.target.value)} data-testid="booking-phone" /></Field>
      </div>
      {people.length > 0 && (
        <div className="space-y-2">
          <p className="field-label">Other participants (optional)</p>
          {people.map((p, i) => (
            <div key={i} className="grid grid-cols-[1fr_80px] gap-2">
              <input className="field" placeholder={`Participant ${i + 2} name`} value={p.name} onChange={(e) => setPeople(people.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} data-testid={`booking-participant-${i}-name`} />
              <input className="field" type="number" placeholder="Age" value={p.age} onChange={(e) => setPeople(people.map((x, j) => (j === i ? { ...x, age: e.target.value } : x)))} />
            </div>
          ))}
        </div>
      )}
      <Field label="Special requirements" id="bk-req"><textarea id="bk-req" rows={3} className="field" value={f.special_requirements} onChange={(e) => set("special_requirements", e.target.value)} data-testid="booking-requirements" /></Field>
      {unit > 0 && n > 0 && (
        <div className="flex justify-between items-baseline border-t border-black pt-4">
          <span className="text-sm">{n} × {money(unit, exp.currency)}</span>
          <span className="text-xl font-bold" data-testid="booking-total">{money(unit * n, exp.currency)}</span>
        </div>
      )}
      <p className="flex gap-2 text-xs text-[#666]"><Info className="h-4 w-4 shrink-0" />This is a booking request. It stays “Awaiting Confirmation” until our team confirms availability and payment details.</p>
      <Consent f={f} set={set} errors={errors} prefix="booking" />
      {fm.error && <p className="border border-black p-3 text-sm" role="alert" data-testid="booking-error">{fm.error}</p>}
      <button type="submit" className="btn-primary w-full" disabled={fm.busy} data-testid="booking-submit">{fm.busy && <Loader2 className="h-4 w-4 animate-spin" />}Request Booking</button>
    </form>
  );
}
